import { useQuery } from "react-query";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../services/firebase";
import { PeriodStats, TopicStats } from "../types";

// 1週間ぶんの集計
export interface WeeklyBreakdown {
  weekLabel: string; // "2/3〜2/9" （期間内の表示範囲）
  startDate: string; // 期間内に切り詰めた開始日 "2026-02-03"
  endDate: string; // 期間内に切り詰めた終了日 "2026-02-09"
  stats: PeriodStats;
}

// 期間統計の結果
export interface PeriodStatsResult {
  from: string; // "2026-02-01"
  to: string; // "2026-02-28"
  weeks: WeeklyBreakdown[];
  total: PeriodStats;
}

// 期間プリセット
export type PeriodPreset =
  | "thisWeek"
  | "last4Weeks"
  | "last12Weeks"
  | "last26Weeks"
  | "last52Weeks"
  | "custom";

export interface PeriodPresetOption {
  value: PeriodPreset;
  label: string;
}

export const PERIOD_PRESET_OPTIONS: PeriodPresetOption[] = [
  { value: "thisWeek", label: "今週" },
  { value: "last4Weeks", label: "過去4週" },
  { value: "last12Weeks", label: "過去12週" },
  { value: "last26Weeks", label: "過去26週（半年）" },
  { value: "last52Weeks", label: "過去52週（1年）" },
  { value: "custom", label: "カスタム" },
];

// JST基準の現在日
const getJstDate = (): Date => {
  const now = new Date();
  return new Date(now.getTime() + 9 * 60 * 60 * 1000);
};

// Date を YYYY-MM-DD に
const formatDate = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// 今日のJST日付
export const getTodayDateString = (): string => {
  return formatDate(getJstDate());
};

// プリセットから [from, to] を計算
export const resolveDateRangeFromPreset = (
  preset: PeriodPreset,
  customFrom?: string,
  customTo?: string
): { from: string; to: string } => {
  const today = getJstDate();
  const todayStr = formatDate(today);

  switch (preset) {
    case "thisWeek": {
      const monday = getMondayOfWeek(todayStr);
      return { from: monday, to: todayStr };
    }
    case "last4Weeks": {
      const start = new Date(today);
      start.setUTCDate(start.getUTCDate() - 27);
      return { from: formatDate(start), to: todayStr };
    }
    case "last12Weeks": {
      const start = new Date(today);
      start.setUTCDate(start.getUTCDate() - 12 * 7 + 1);
      return { from: formatDate(start), to: todayStr };
    }
    case "last26Weeks": {
      const start = new Date(today);
      start.setUTCDate(start.getUTCDate() - 26 * 7 + 1);
      return { from: formatDate(start), to: todayStr };
    }
    case "last52Weeks": {
      const start = new Date(today);
      start.setUTCDate(start.getUTCDate() - 52 * 7 + 1);
      return { from: formatDate(start), to: todayStr };
    }
    case "custom": {
      const from = customFrom || todayStr;
      const to = customTo || todayStr;
      return { from, to };
    }
  }
};

// 空のPeriodStats
const createEmptyPeriodStats = (): PeriodStats => ({
  totalResponses: 0,
  totalLaunches: 0,
  uniqueLaunches: 0,
  totalViews: 0,
  byGenre: {},
  byDifficulty: {},
  byTopic: {},
});

// 1日分のデータをPeriodStatsに加算
const addDailyDataToPeriodStats = (stats: PeriodStats, data: Record<string, unknown>): void => {
  stats.totalResponses += (data.totalResponses as number) || 0;
  stats.totalLaunches += (data.totalLaunches as number) || 0;
  stats.uniqueLaunches += (data.uniqueLaunches as number) || 0;
  stats.totalViews += (data.totalViews as number) || 0;

  Object.entries((data.byGenre as Record<string, number>) || {}).forEach(([genre, count]) => {
    stats.byGenre[genre] = (stats.byGenre[genre] || 0) + count;
  });

  Object.entries((data.byDifficulty as Record<string, number>) || {}).forEach(
    ([difficulty, count]) => {
      stats.byDifficulty[Number(difficulty)] =
        (stats.byDifficulty[Number(difficulty)] || 0) + count;
    }
  );

  Object.entries((data.byTopic as Record<string, TopicStats>) || {}).forEach(
    ([topicId, topicStats]) => {
      if (!stats.byTopic[topicId]) {
        stats.byTopic[topicId] = {
          count: 0,
          viewCount: 0,
          genre: topicStats.genre,
          difficulty: topicStats.difficulty,
          contentPreview: topicStats.contentPreview,
          theme: topicStats.theme,
        };
      }
      stats.byTopic[topicId].count += topicStats.count;
      stats.byTopic[topicId].viewCount += topicStats.viewCount || 0;
    }
  );
};

// 日付から月曜日（週の開始日）を取得
const getMondayOfWeek = (dateStr: string): string => {
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  const dayOfWeek = date.getUTCDay(); // 0=日, 1=月, ..., 6=土
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  date.setUTCDate(date.getUTCDate() - daysFromMonday);
  return formatDate(date);
};

// 月曜日から日曜日（週の終了日）を取得
const getSundayOfWeek = (mondayStr: string): string => {
  const [year, month, day] = mondayStr.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + 6);
  return formatDate(date);
};

// 週ラベルをフォーマット: "2/3〜2/9"
const formatWeekLabel = (startDate: string, endDate: string): string => {
  const [, startMonth, startDay] = startDate.split("-").map(Number);
  const [, endMonth, endDay] = endDate.split("-").map(Number);
  return `${startMonth}/${startDay}〜${endMonth}/${endDay}`;
};

// from〜to の期間に含まれる年月リストを返す（例: ["2026-02", "2026-03"]）
const getMonthsBetween = (from: string, to: string): string[] => {
  const months: string[] = [];
  const [startYear, startMonth] = from.split("-").map(Number);
  const [endYear, endMonth] = to.split("-").map(Number);

  let year = startYear;
  let month = startMonth;

  while (year < endYear || (year === endYear && month <= endMonth)) {
    months.push(`${year}-${String(month).padStart(2, "0")}`);
    month++;
    if (month > 12) {
      month = 1;
      year++;
    }
  }

  return months;
};

// 期間データ取得本体
const fetchPeriodStats = async (from: string, to: string): Promise<PeriodStatsResult> => {
  const total = createEmptyPeriodStats();
  const weekMap = new Map<string, { startDate: string; endDate: string; stats: PeriodStats }>();

  const months = getMonthsBetween(from, to);

  try {
    const snapshots = await Promise.all(
      months.map((m) => getDocs(collection(db, "stats", "daily", m)))
    );

    snapshots.forEach((snapshot) => {
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const dateStr = data.date as string;
        if (!dateStr) return;
        if (dateStr < from || dateStr > to) return;

        // 期間合計に加算
        addDailyDataToPeriodStats(total, data);

        // 週単位に振り分け（期間外の日には伸ばさない）
        const monday = getMondayOfWeek(dateStr);
        if (!weekMap.has(monday)) {
          const sunday = getSundayOfWeek(monday);
          const clampedStart = monday < from ? from : monday;
          const clampedEnd = sunday > to ? to : sunday;
          weekMap.set(monday, {
            startDate: clampedStart,
            endDate: clampedEnd,
            stats: createEmptyPeriodStats(),
          });
        }
        addDailyDataToPeriodStats(weekMap.get(monday)!.stats, data);
      });
    });
  } catch (error) {
    console.error(`Error fetching period stats for ${from}〜${to}:`, error);
  }

  const sortedWeeks = Array.from(weekMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, weekData]) => ({
      weekLabel: formatWeekLabel(weekData.startDate, weekData.endDate),
      startDate: weekData.startDate,
      endDate: weekData.endDate,
      stats: weekData.stats,
    }));

  return { from, to, weeks: sortedWeeks, total };
};

/**
 * 期間統計取得Hook
 *
 * 指定した期間 [from, to] のデイリー集計を読み込み、
 * - 期間合計（total）
 * - 週別ブレイクダウン（weeks、月曜始まり、期間外には伸びない）
 * を返す。
 */
export const usePeriodStats = (from: string, to: string) => {
  return useQuery<PeriodStatsResult, Error>(
    ["periodStats", from, to],
    () => fetchPeriodStats(from, to),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      retry: 2,
    }
  );
};
